import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2 } from "lucide-react";
import { useDeliveryOrders } from "@/hooks/useDeliveryOrders";
import { salesOrdersApi } from "@/services/salesDocumentsApi";
import {
  DeliveryOrderListItem,
  DeliveryOrderStatus,
} from "@/types/salesDocuments";
import { format } from "date-fns";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

interface DeliveryOrderPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDeliveryOrder: (deliveryOrder: DeliveryOrderListItem) => void;
  excludeInvoiced?: boolean;
}

export const DeliveryOrderPickerModal = ({
  isOpen,
  onClose,
  onSelectDeliveryOrder,
  excludeInvoiced = true,
}: DeliveryOrderPickerModalProps) => {
  const { formatTotalAmount } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");
  const [salesOrderNumbers, setSalesOrderNumbers] = useState<{
    [key: string]: string;
  }>({});
  const [isLoadingSOs, setIsLoadingSOs] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: deliveryOrdersData,
    isLoading,
    error,
  } = useDeliveryOrders({
    search: searchTerm,
  });

  // Filter delivery orders based on status
  const filteredDeliveryOrders =
    deliveryOrdersData?.data?.filter((deliveryOrder: any) => {
      const canBeInvoiced =
        deliveryOrder.status === DeliveryOrderStatus.INVOICE_PENDING ||
        deliveryOrder.status === DeliveryOrderStatus.PENDING;

      return canBeInvoiced;
    }) || [];

  // Fetch SO numbers for DOs that have sourceSalesOrderId
  const fetchSOs = React.useCallback(async () => {
    if (isLoadingSOs) return; // Prevent multiple simultaneous calls

    setIsLoadingSOs(true);
    const newSalesOrderNumbers: { [key: string]: string } = {};

    for (const deliveryOrder of filteredDeliveryOrders) {
      if (
        deliveryOrder.sourceSalesOrderId &&
        !newSalesOrderNumbers[deliveryOrder.id]
      ) {
        try {
          const salesOrder = await queryClient.fetchQuery({
            queryKey: [
              "salesOrders",
              "detail",
              deliveryOrder.sourceSalesOrderId!,
            ],
            queryFn: () =>
              salesOrdersApi.getById(deliveryOrder.sourceSalesOrderId!),
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
          newSalesOrderNumbers[deliveryOrder.id] = salesOrder.codeNumber;
        } catch (error) {
          // Error fetching sales order, but continue with other DOs
        }
      }
    }

    setSalesOrderNumbers(newSalesOrderNumbers);
    setIsLoadingSOs(false);
  }, [queryClient]);

  // Prevent unnecessary re-renders and cleanup
  React.useEffect(() => {
    if (filteredDeliveryOrders.length > 0 && !isLoadingSOs) {
      fetchSOs();
    }
  }, [filteredDeliveryOrders.length]);

  const getStatusColor = (status: DeliveryOrderStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case DeliveryOrderStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case DeliveryOrderStatus.PENDING:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case DeliveryOrderStatus.IN_TRANSIT:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case DeliveryOrderStatus.DELIVERED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case DeliveryOrderStatus.INVOICE_PENDING:
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case DeliveryOrderStatus.ISSUED_INVOICE:
        return `${baseStyle} bg-purple-100 text-purple-800`;
      case DeliveryOrderStatus.CANCELLED:
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const handleSelect = (deliveryOrder: DeliveryOrderListItem) => {
    onSelectDeliveryOrder(deliveryOrder);
    onClose();
    setSearchTerm("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Select Delivery Order</DialogTitle>
          <DialogDescription>
            Search and select a delivery order to create an invoice from.
            {excludeInvoiced &&
              " Only showing delivery orders that haven't been invoiced."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by delivery order number, buyer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              aria-label="Search delivery orders"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white dark:bg-gray-950 sticky top-0">
                <TableRow>
                  <TableHead className="w-[150px]">DO Number</TableHead>
                  <TableHead className="w-[180px]">Buyer Name</TableHead>
                  <TableHead className="w-[120px]">Delivery Date</TableHead>
                  <TableHead className="w-[120px]">SO Number</TableHead>
                  <TableHead className="w-[120px]">Amount (RM)</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        <p className="mt-2 text-sm text-muted-foreground">
                          Loading delivery orders...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-red-500 py-8"
                      >
                        Error loading delivery orders
                      </TableCell>
                    </TableRow>
                  ) : filteredDeliveryOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <p className="text-muted-foreground">
                          {searchTerm
                            ? "No delivery orders found matching your search"
                            : excludeInvoiced
                              ? "No available delivery orders to invoice"
                              : "No delivery orders found"}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDeliveryOrders.map((deliveryOrder) => (
                      <TableRow
                        key={deliveryOrder.id}
                        className="hover:bg-muted"
                      >
                        <TableCell className="w-[150px] font-medium">
                          {deliveryOrder.codeNumber}
                          {salesOrderNumbers[deliveryOrder.id] && (
                            <div className="text-xs text-gray-500">
                              From: {salesOrderNumbers[deliveryOrder.id]}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="w-[180px]">
                          {deliveryOrder.buyerName || "N/A"}
                          <div className="text-xs text-gray-500">
                            {deliveryOrder.buyerCode}
                          </div>
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {format(
                            new Date(deliveryOrder.deliveryDate),
                            "dd/MM/yyyy",
                          )}
                        </TableCell>
                        <TableCell className="w-[120px]">
                          {salesOrderNumbers[deliveryOrder.id] || "N/A"}
                        </TableCell>
                        <TableCell className="w-[120px] text-right">
                          {formatTotalAmount(deliveryOrder.totalPayableAmount)}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Badge
                            className={getStatusColor(deliveryOrder.status)}
                          >
                            {deliveryOrder.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => handleSelect(deliveryOrder)}
                            aria-label={`Select delivery order ${deliveryOrder.codeNumber}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryOrderPickerModal;
